import { ArrowLeft, Printer, Download, MapPin, Phone, Mail, Globe, Rocket, ShieldCheck, BadgeCheck } from "lucide-react";
import html2pdf from "html2pdf.js";

/**
 * 2026 branded invoice — navy-blue full-bleed + dark-orange accents + cream type.
 * Designed to feel like a premium space-mission document.
 *
 * Palette: navy #0B192C · navy-2 #112842 · orange #E65C00 · cream #F4EAD5
 */
export default function BrandedInvoice({ invoice, onBack }) {
  function downloadPdf() {
    const el = document.getElementById("branded-invoice-paper");
    if (!el) return;
    const filename = `${invoice.invoice_number || "invoice"}-space-appliance-repair.pdf`;
    html2pdf()
      .set({
        margin: 0,
        filename,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#002060" },
        jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
        pagebreak: { mode: ["css", "legacy"] },
      })
      .from(el)
      .save();
  }

  return (
    <div className="min-h-screen bg-[#002060] print:bg-[#FFFFFF] py-4 print:py-0">
      {/* Toolbar (hidden in print) */}
      <div className="max-w-3xl mx-auto px-4 mb-4 flex items-center justify-between print:hidden">
        <button
          onClick={onBack}
          data-testid="branded-back"
          className="inline-flex items-center gap-2 text-sm text-[#FFFFFF]/80 hover:text-[#FF6600] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={() => window.print()}
            data-testid="branded-print"
            className="inline-flex items-center gap-2 bg-transparent text-[#FFFFFF] hover:bg-[#FFFFFF]/10 border border-[#FFFFFF]/40 px-4 h-10 rounded-md text-xs uppercase tracking-widest font-semibold transition"
          >
            <Printer className="w-3.5 h-3.5" /> Print
          </button>
          <button
            onClick={downloadPdf}
            data-testid="branded-download"
            className="inline-flex items-center gap-2 bg-[#FF6600] text-[#FFFFFF] hover:bg-[#FF6600]/90 px-4 h-10 rounded-md text-xs uppercase tracking-widest font-semibold transition"
          >
            <Download className="w-3.5 h-3.5" /> Download PDF
          </button>
        </div>
      </div>

      {/* Paper — white bg with navy headers + orange CTAs */}
      <article
        id="branded-invoice-paper"
        className="max-w-3xl mx-auto bg-[#FFFFFF] text-[#002060] shadow-2xl print:shadow-none rounded-md print:rounded-none overflow-hidden border-2 border-[#002060]"
        style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
      >
        {/* ============ HEADER (compact) ============ */}
        <header className="relative bg-[#002060] px-8 pt-3 pb-5 overflow-hidden">
          <div className="absolute top-0 right-0 h-1 w-1/2 bg-[#FF6600]" />
          <div className="relative flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <img
                src="/brand/logo.png"
                alt="Space Appliance Repair"
                className="w-14 h-14 object-contain bg-[#FFFFFF] rounded-full p-1 border-2 border-[#FF6600] shrink-0"
              />
              <div>
                <div className="text-[9px] uppercase tracking-[0.35em] text-[#FF6600] font-semibold">Mission control · San Antonio</div>
                <div className="text-[#FFFFFF] text-2xl mt-1 uppercase tracking-tight leading-none font-bold">Space Appliance Repair</div>
                <div className="text-[#FFFFFF]/80 text-[10px] mt-1.5 max-w-xs leading-snug">Family-run since 2019 · Same-day diagnostic · 90-day warranty.</div>
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-[#FF6600] rounded-full">
                <span className="text-[9px] uppercase tracking-[0.3em] text-[#FFFFFF] font-bold">Invoice</span>
              </div>
              <div className="text-[#FFFFFF] text-2xl num mt-1 font-bold leading-none">{invoice.invoice_number}</div>
              <div className="text-[#FFFFFF]/70 text-[10px] mt-1.5 uppercase tracking-widest">Issued {formatDate(invoice.created_at)}</div>
              {invoice.payment_terms && (
                <div className="text-[#FFFFFF]/60 text-[9px] mt-0.5 uppercase tracking-widest">Terms · {invoice.payment_terms}</div>
              )}
            </div>
          </div>
        </header>

        {/* Contact strip */}
        <div className="bg-[#002060] border-y border-[#FFFFFF]/20 px-8 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[9px] uppercase tracking-widest text-[#FFFFFF]/90">
          <span className="inline-flex items-center gap-1"><MapPin className="w-2.5 h-2.5 text-[#FF6600]" /> 5504 Bandera Rd, San Antonio, TX 78238</span>
          <span className="inline-flex items-center gap-1"><Phone className="w-2.5 h-2.5 text-[#FF6600]" /> (210) 800-1710</span>
          <span className="inline-flex items-center gap-1"><Mail className="w-2.5 h-2.5 text-[#FF6600]" /> hello@spaceappliancerepair.com</span>
          <span className="inline-flex items-center gap-1"><Globe className="w-2.5 h-2.5 text-[#FF6600]" /> spaceappliancerepair.com</span>
        </div>

        {/* ============ BILLED-TO + APPLIANCE INFO ============ */}
        <div className="px-8 py-4 grid sm:grid-cols-2 gap-3">
          <section className="bg-[#002060]/5 border border-[#002060]/15 rounded-md p-3">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF6600] font-semibold">Billed to</div>
            <div className="text-base mt-1.5 font-semibold leading-tight">{invoice.customer_name || "—"}</div>
            <div className="text-sm text-[#002060]/70 mt-2 leading-relaxed">{invoice.address || "—"}</div>
            {invoice.customer_phone && (
              <div className="flex items-center gap-1.5 text-sm text-[#002060]/70 mt-1.5">
                <Phone className="w-3 h-3 text-[#FF6600]" /> {invoice.customer_phone}
              </div>
            )}
          </section>

          <section className="bg-[#002060] text-[#FFFFFF] border-2 border-[#FF6600] rounded-md p-3 relative overflow-hidden">
            <div
              className="absolute inset-0 opacity-30 pointer-events-none"
              style={{}}
            />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF6600] font-semibold">Appliance info</div>
              <div className="text-base mt-1.5 capitalize leading-tight font-semibold text-[#FFFFFF]">{invoice.appliance || "Appliance"}</div>
              <div className="grid grid-cols-2 gap-3 mt-2 text-sm">
                <KV k="Make" v={invoice.appliance_make || "—"} onDark />
                <KV k="Model" v={invoice.appliance_model || "—"} onDark />
              </div>
            </div>
          </section>
        </div>

        {/* Meta strip */}
        <div className="px-8 -mt-2 mb-5 flex flex-wrap items-center gap-x-6 gap-y-1.5 text-[10px] uppercase tracking-widest text-[#002060]/50">
          <span><b className="text-[#002060]">Service:</b> {invoice.appliance ? `${invoice.appliance} repair` : "Appliance repair"}</span>
          <span><b className="text-[#002060]">Status:</b> <StatusInline status={invoice.status} /></span>
          {invoice.paid_at && <span><b className="text-[#002060]">Paid on:</b> {formatDate(invoice.paid_at)}</span>}
        </div>

        {/* ============ LINE ITEMS ============ */}
        <div className="px-8">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF6600] font-semibold mb-2">Itemized work</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-[#FF6600] text-[#002060]/50 text-[10px] uppercase tracking-widest">
                <th className="text-left py-2 font-semibold">Description</th>
                <th className="text-right py-2 font-semibold w-16">Qty</th>
                <th className="text-right py-2 font-semibold w-24">Unit cost</th>
                <th className="text-right py-2 font-semibold w-24">Total</th>
              </tr>
            </thead>
            <tbody>
              {invoice.line_items.map((li, i) => (
                <tr key={i} className="border-b border-[#002060]/10">
                  <td className="py-1.5 pr-3">
                    <div>{li.description}</div>
                    {li.kind && li.kind !== "other" && (
                      <div className="text-[10px] uppercase tracking-widest text-[#FF6600]/80 mt-0.5">{li.kind}</div>
                    )}
                  </td>
                  <td className="py-1.5 text-right num text-[#002060]/80">{li.qty}</td>
                  <td className="py-1.5 text-right num text-[#002060]/80">${Number(li.unit_price).toFixed(2)}</td>
                  <td className="py-1.5 text-right num font-semibold">${(Number(li.qty) * Number(li.unit_price)).toFixed(2)}</td>
                </tr>
              ))}
              {invoice.diagnostic_fee_waived && (
                <tr className="border-b border-[#002060]/10 bg-[#FF6600]/5">
                  <td className="py-1.5 pr-3 text-[#FF6600] italic">
                    Diagnostic fee — <b>waived</b> as a courtesy
                    <div className="text-[10px] uppercase tracking-widest text-[#FF6600]/70 mt-0.5">Complimentary</div>
                  </td>
                  <td className="py-1.5 text-right num text-[#002060]/30">1</td>
                  <td className="py-1.5 text-right num text-[#002060]/30 line-through">$89.00</td>
                  <td className="py-1.5 text-right num text-[#FF6600] font-semibold">$0.00</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ============ NOTES + PRICING BREAKDOWN ============ */}
        <div className="px-8 py-4 grid sm:grid-cols-5 gap-3">
          <div className="sm:col-span-3">
            {invoice.notes && (
              <div className="bg-[#002060]/5 border-l-4 border-[#FF6600] p-4 text-sm text-[#002060]/80">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF6600] mb-1 font-semibold">Service notes</div>
                {invoice.notes}
              </div>
            )}
          </div>
          <div className="sm:col-span-2 space-y-1.5 text-sm">
            <TotalRow k="Subtotal" v={invoice.subtotal} />
            {Number(invoice.service_fee) > 0 && <TotalRow k="Service fees" v={invoice.service_fee} />}
            {Number(invoice.discount) > 0 && <TotalRow k="Discount" v={-Math.abs(invoice.discount)} tone="emerald" />}
            {Number(invoice.tax_rate) > 0 && (
              <TotalRow k={`Tax (${(invoice.tax_rate * 100).toFixed(2)}%)`} v={invoice.tax} />
            )}
            {Number(invoice.card_fee_rate) > 0 && Number(invoice.card_fee) > 0 && (
              <TotalRow k={`Card fee (${(invoice.card_fee_rate * 100).toFixed(2)}%)`} v={invoice.card_fee} />
            )}
          </div>
        </div>

        {/* ============ TOTAL AMOUNT OWED ============ */}
        <div className="px-8 pb-3">
          <div className="bg-[#002060] text-[#FFFFFF] rounded-md p-4 relative overflow-hidden border-2 border-[#FF6600]">
            <div className="relative flex flex-wrap items-end justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.35em] font-bold text-[#FF6600]">
                  {Number(invoice.amount_paid || 0) > 0 ? "Balance Due" : "Total amount owed"}
                </div>
                <div className="text-[#FFFFFF]/80 text-xs mt-1.5 uppercase tracking-widest">{invoice.payment_terms || "Due on receipt"}</div>
                {/* Payment ledger preview */}
                {(Number(invoice.amount_paid || 0) > 0 || (invoice.payments_public && invoice.payments_public.length > 0)) && (
                  <div className="mt-2 space-y-0.5 text-[11px]">
                    <div className="text-[#FFFFFF]/70">
                      <span className="uppercase tracking-widest text-[9px]">Total: </span>
                      <span className="num text-[#FFFFFF]">${Number(invoice.total).toFixed(2)}</span>
                    </div>
                    {(invoice.payments_public || []).map((p, i) => (
                      <div key={i} className="text-[#FFFFFF]/70">
                        <span className="uppercase tracking-widest text-[9px]">
                          {p.payment_applied_to === "deposit" ? "Deposit:" : "Paid:"}
                        </span>{" "}
                        <span className="num text-[#FF6600] font-semibold">${Number(p.amount).toFixed(2)}</span>{" "}
                        <span className="text-[#FFFFFF]/60 capitalize">({(p.payment_method || "").replace("_", " ")}{p.check_number ? ` #${p.check_number}` : ""})</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {["Visa", "Mastercard", "Amex", "Cash", "Check", "Zelle"].map((m) => (
                    <span
                      key={m}
                      className="text-[10px] uppercase tracking-widest border border-[#FFFFFF]/40 text-[#FFFFFF] px-2 py-1 rounded-full bg-[#FFFFFF]/10"
                    >
                      {m}
                    </span>
                  ))}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-3xl num leading-none font-bold drop-shadow text-[#FFFFFF]">
                  ${Number(invoice.balance_due ?? invoice.total).toFixed(2)}
                </div>
                {invoice.status === "paid" ? (
                  <div className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 bg-[#FF6600] text-[#FFFFFF] border border-[#FF6600] text-[10px] uppercase tracking-widest font-bold rounded-full">
                    <BadgeCheck className="w-3 h-3" /> Paid in full
                  </div>
                ) : invoice.status === "partially_paid" ? (
                  <div className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 bg-[#FFFFFF] text-[#FF6600] border border-[#FF6600] text-[10px] uppercase tracking-widest font-bold rounded-full">
                    Partial paid — balance due
                  </div>
                ) : invoice.payment_type === "split" && invoice.deposit_amount_required ? (
                  <div className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 bg-[#FF6600] text-[#FFFFFF] border border-[#FF6600] text-[10px] uppercase tracking-widest font-bold rounded-full">
                    Deposit ${Number(invoice.deposit_amount_required).toFixed(2)} due
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 bg-[#FFFFFF] text-[#002060] border border-[#FFFFFF] text-[10px] uppercase tracking-widest font-bold rounded-full">
                    Awaiting payment
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ============ WARRANTY (compact, single-page friendly) ============ */}
        <div className="px-8 pb-3">
          <section className="bg-[#FFFFFF] border border-[#002060]/25 rounded-md p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-[#FF6600]" />
                <div className="text-[9px] uppercase tracking-[0.3em] text-[#FF6600] font-bold">90-Day Limited Warranty</div>
              </div>
              <div className="text-[9px] uppercase tracking-widest text-[#002060]/60">Claim ref · <span className="num font-bold text-[#FF6600]">{invoice.invoice_number}</span></div>
            </div>
            <p className="text-[11px] text-[#002060]/85 mt-1.5 leading-snug">
              <strong>Coverage:</strong> If the same part or issue recurs within 90 days, Space techs return at no cost.{" "}
              <strong>Exclusions:</strong> only the parts/labor on this invoice — not new issues, misuse, physical damage, or repairs by other parties.{" "}
              <strong>Claims:</strong> call (210) 800-1710 with this invoice number.
            </p>
          </section>
        </div>

        {/* ============ THANK YOU FOOTER (compact) ============ */}
        <footer className="bg-[#002060] border-t-2 border-[#FF6600] px-8 py-3 text-center">
          <div className="text-[9px] uppercase tracking-[0.35em] text-[#FF6600] font-bold">Thank you for choosing Space!</div>
          <p className="text-[#FFFFFF]/85 text-[11px] mt-1 max-w-xl mx-auto leading-snug">
            We appreciate you trusting us with your home appliance needs.
          </p>
          <div className="text-[9px] uppercase tracking-widest text-[#FFFFFF]/60 num mt-1.5">
            {invoice.invoice_number} · spaceappliancerepair.com · (210) 800-1710
          </div>
        </footer>
      </article>

      <style>{`
        @media print {
          @page { margin: 0; size: auto; }
          body { background: #002060 !important; }
          .num { font-variant-numeric: tabular-nums; }
        }
      `}</style>
    </div>
  );
}

function StatusInline({ status }) {
  const map = {
    draft: { label: "DRAFT", color: "text-[#FF6600]" },
    sent: { label: "SENT", color: "text-[#002060]" },
    paid: { label: "PAID", color: "text-[#FF6600]" },
    void: { label: "VOID", color: "text-[#002060]/40" },
  };
  const m = map[status] || { label: (status || "").toUpperCase(), color: "text-[#002060]" };
  return <span className={`font-bold ${m.color}`}>{m.label}</span>;
}

function KV({ k, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#FF6600]/90 font-bold">{k}</div>
      <div className="font-medium mt-0.5 truncate text-[#002060]">{v}</div>
    </div>
  );
}

function TotalRow({ k, v, tone = "default" }) {
  const tones = { default: "text-[#002060]/80", emerald: "text-[#FF6600]" };
  return (
    <div className={`flex items-center justify-between ${tones[tone]}`}>
      <span className="text-[#002060]/60">{k}</span>
      <span className="num text-[#002060]">
        {Number(v) < 0 ? "−$" : "$"}{Math.abs(Number(v)).toFixed(2)}
      </span>
    </div>
  );
}

function Term({ k, children }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-widest text-[#FF6600] font-bold">{k}</dt>
      <dd className="text-[#002060]/80 mt-0.5 leading-relaxed">{children}</dd>
    </div>
  );
}

function formatDate(s) {
  if (!s) return "";
  const d = new Date(s);
  if (isNaN(d.valueOf())) return s;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
